import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Lock, Eye, FloppyDisk, Info } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Creator-facing settings panel for Private Shows + Voyeur viewing.
 *
 * Phase A — settings storage UI only. The actual private-show
 * request/accept/countdown flow lands in Phase B and consumes the values
 * configured here.
 */
const PrivateShowSettingsCard = () => {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/api/streaming/private-settings`, { withCredentials: true });
        if (!cancelled) setSettings(res.data);
      } catch (e) {
        if (!cancelled) toast.error('Failed to load private-show settings');
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const update = (patch) => {
    setSettings(s => ({ ...s, ...patch }));
    setDirty(true);
  };

  const save = async () => {
    if (!settings) return;
    // Light client-side validation; backend clamps too.
    if (settings.allow_private) {
      if (settings.private_price_per_minute < 1 || settings.private_price_per_minute > 1000) {
        toast.error('Private price must be 1–1000 tokens/min'); return;
      }
      if (settings.private_min_minutes < 1 || settings.private_min_minutes > 60) {
        toast.error('Minimum length must be 1–60 minutes'); return;
      }
    }
    if (settings.allow_voyeurs) {
      if (settings.voyeur_price_per_minute < 1 || settings.voyeur_price_per_minute > 1000) {
        toast.error('Voyeur price must be 1–1000 tokens/min'); return;
      }
    }
    setSaving(true);
    try {
      const res = await axios.put(`${API}/api/streaming/private-settings`, settings, { withCredentials: true });
      setSettings(res.data);
      setDirty(false);
      toast.success('Private-show settings saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (!settings) return <div className="text-slate-500 text-sm">Loading private-show settings…</div>;

  const privateOn = !!settings.allow_private;
  const voyeursOn = !!settings.allow_voyeurs;

  return (
    <div className="glass-light rounded-2xl p-6" data-testid="private-show-settings-card">
      <div className="flex items-center gap-2 mb-3">
        <Lock weight="fill" className="w-6 h-6 text-indigo-400" />
        <h3 className="font-heading text-xl font-bold text-white">Private Shows &amp; Voyeurs</h3>
        <span className="ml-auto px-2 py-0.5 bg-indigo-500/20 text-indigo-300 text-xs font-bold rounded-full">
          {privateOn ? 'Enabled' : 'Off'}
        </span>
      </div>

      <p className="text-slate-400 text-sm mb-5">
        Let viewers pay you per-minute for one-on-one private time, with optional voyeurs
        watching at a discount. Configure here — the live request flow ships in the next
        update.
      </p>

      {/* Private show enable */}
      <label className="flex items-start gap-3 mb-5 p-4 rounded-xl bg-slate-900/60 border border-white/5 cursor-pointer hover:border-indigo-500/30 transition-colors">
        <input
          type="checkbox"
          checked={privateOn}
          onChange={(e) => update({ allow_private: e.target.checked })}
          className="mt-1 accent-indigo-500"
          data-testid="allow-private-toggle"
        />
        <div>
          <p className="text-white font-semibold">Allow private show requests</p>
          <p className="text-xs text-slate-500 mt-0.5">
            Viewers will see a &quot;Request Private&quot; button on your live stream.
            You decide accept / decline each time.
          </p>
        </div>
      </label>

      {/* Private show price + min minutes */}
      <div className={`grid sm:grid-cols-2 gap-3 mb-5 transition-opacity ${privateOn ? 'opacity-100' : 'opacity-40 pointer-events-none'}`}>
        <div>
          <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Private rate (tokens / minute)</label>
          <input
            type="number"
            min={1}
            max={1000}
            value={settings.private_price_per_minute}
            onChange={(e) => update({ private_price_per_minute: parseInt(e.target.value) || 0 })}
            disabled={!privateOn}
            className="w-full mt-1 bg-slate-800 border border-white/10 rounded-lg px-3 py-2 text-white"
            data-testid="private-price-input"
          />
        </div>
        <div>
          <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Minimum length (minutes)</label>
          <input
            type="number"
            min={1}
            max={60}
            value={settings.private_min_minutes}
            onChange={(e) => update({ private_min_minutes: parseInt(e.target.value) || 0 })}
            disabled={!privateOn}
            className="w-full mt-1 bg-slate-800 border border-white/10 rounded-lg px-3 py-2 text-white"
            data-testid="private-min-minutes-input"
          />
          <p className="text-[10px] text-slate-600 mt-1">
            Buyer prepays {settings.private_price_per_minute * settings.private_min_minutes || 0} tokens up front.
          </p>
        </div>
      </div>

      {/* Recording download */}
      <label className={`flex items-start gap-3 mb-5 p-4 rounded-xl bg-slate-900/60 border border-white/5 cursor-pointer transition-colors ${privateOn ? 'hover:border-indigo-500/30' : 'opacity-40 pointer-events-none'}`}>
        <input
          type="checkbox"
          checked={!!settings.allow_private_recording_dl}
          onChange={(e) => update({ allow_private_recording_dl: e.target.checked })}
          disabled={!privateOn}
          className="mt-1 accent-indigo-500"
          data-testid="allow-private-recording-toggle"
        />
        <div>
          <p className="text-white font-semibold">Let the buyer download their private recording</p>
          <p className="text-xs text-slate-500 mt-0.5">
            Only the paying viewer (not voyeurs) gets a link. You still keep the full VOD per the standard rules.
          </p>
        </div>
      </label>

      <div className="border-t border-white/5 my-5" />

      {/* Voyeurs toggle */}
      <div className="flex items-center gap-2 mb-3">
        <Eye weight="fill" className="w-5 h-5 text-amber-400" />
        <h4 className="font-heading text-base font-bold text-white">Voyeurs (optional)</h4>
      </div>

      <label className="flex items-start gap-3 mb-5 p-4 rounded-xl bg-slate-900/60 border border-white/5 cursor-pointer hover:border-amber-500/30 transition-colors">
        <input
          type="checkbox"
          checked={voyeursOn}
          onChange={(e) => update({ allow_voyeurs: e.target.checked })}
          className="mt-1 accent-amber-500"
          data-testid="allow-voyeurs-toggle"
        />
        <div>
          <p className="text-white font-semibold">Allow voyeurs during private shows</p>
          <p className="text-xs text-slate-500 mt-0.5">
            Other viewers can pay a per-minute rate to watch &amp; listen (no chat).
            They get kicked when their balance hits zero.
          </p>
        </div>
      </label>

      <div className={`mb-5 transition-opacity ${voyeursOn ? 'opacity-100' : 'opacity-40 pointer-events-none'}`}>
        <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Voyeur rate (tokens / minute)</label>
        <input
          type="number"
          min={1}
          max={1000}
          value={settings.voyeur_price_per_minute}
          onChange={(e) => update({ voyeur_price_per_minute: parseInt(e.target.value) || 0 })}
          disabled={!voyeursOn}
          className="w-full sm:w-1/2 mt-1 bg-slate-800 border border-white/10 rounded-lg px-3 py-2 text-white"
          data-testid="voyeur-price-input"
        />
      </div>

      <div className="flex items-start gap-2 mb-5 p-3 rounded-xl bg-amber-500/5 border border-amber-500/20">
        <Info weight="fill" className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
        <p className="text-xs text-amber-200/80 leading-relaxed">
          <strong>Heads-up:</strong> the actual private show <em>flow</em> (viewer request,
          your accept / decline modal, countdown timer, voyeur kick-at-zero, extension prompts)
          ships in the next update. Your settings here will be live the moment it does.
        </p>
      </div>

      <button
        onClick={save}
        disabled={!dirty || saving}
        className="px-5 py-2.5 bg-indigo-500 text-white rounded-xl font-semibold hover:bg-indigo-600 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2"
        data-testid="save-private-settings-btn"
      >
        <FloppyDisk weight="fill" className="w-4 h-4" />
        {saving ? 'Saving…' : (dirty ? 'Save changes' : 'Saved')}
      </button>
    </div>
  );
};

export default PrivateShowSettingsCard;
